import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { LineChart } from '@mui/x-charts/LineChart';
import { useState, useEffect } from 'react';
import createUserService from '../utils/UserService';
import { useLanguage } from '../contexts/LanguageContext';  // ✅ Import Language Context


function AreaGradient({ color, id }: { color: string; id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

function getMonthsInYear() {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date(0, i);
    const monthName = date.toLocaleDateString('en-US', {
      month: 'short',
    });
    months.push(monthName);
  }
  return months;
}

export default function SessionsChart() {
  const theme = useTheme();
  const data = getMonthsInYear();
  const { translations } = useLanguage();  // ✅ Get translations


  const colorPalette = [
    theme.palette.primary.light,
    theme.palette.primary.dark,
  ];


  var [rewardData, setRewardData] = useState([]);
  var [visitData, setVisitData] = useState([]);


  const UserService = createUserService();

  useEffect(() => {
    UserService.getStoresActionsChartStats().then((response) => {
      if (response.status === 200) {
        setRewardData(response.data.rewards);
        setVisitData(response.data.visits);
      }
    })
  }, []);

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
        {translations.store_actions_section.title}  {/* ✅ Translated title */}
        </Typography>
        <Stack sx={{ justifyContent: 'space-between' }}>
          <Stack
            direction="row"
            sx={{
              alignContent: { xs: 'center', sm: 'flex-start' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h4" component="p">
              {visitData?.reduce((a, b) => a + b, 0) + rewardData?.reduce((a, b) => a + b, 0)}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {translations.store_actions_section.visits_per_month}  {/* ✅ Translated description */}
          </Typography>
        </Stack>
        <LineChart
          colors={colorPalette}
          xAxis={[
            {
              scaleType: 'point',
              data,
              tickInterval: (index, i) => (i + 1) % 1 === 0,
            },
          ]}
          series={[
            {
              id: 'visit',
              label: translations.store_actions_section.visit_label,  // ✅ Translated label
              showMark: false,
              curve: 'linear',
              stack: 'total',
              area: true,
              stackOrder: 'ascending',
              data: visitData,
            },
            {
              id: 'reward',
              label: translations.store_actions_section.reward_label,  // ✅ Translated label
              showMark: false,
              curve: 'linear',
              stack: 'total',
              area: true,
              stackOrder: 'ascending',
              data: rewardData,
            }
          ]}
          height={250}
          margin={{ left: 50, right: 20, top: 20, bottom: 20 }}
          grid={{ horizontal: true }}
          sx={{
            '& .MuiAreaElement-series-organic': {
              fill: "url('#organic')",
            },
            '& .MuiAreaElement-series-direct': {
              fill: "url('#direct')",
            },
          }}
          slotProps={{
            legend: {
              hidden: true,
            },
          }}
        >
          <AreaGradient color={theme.palette.primary.dark} id="visit" />
          <AreaGradient color={theme.palette.primary.light} id="reward" />
        </LineChart>
      </CardContent>
    </Card>
  );
}
